import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { formatEther, formatUnits, parseEther, parseUnits } from 'viem'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { CountdownTimer } from '@/components/escrow/CountdownTimer'
import { useDealDetails, parseDealParam } from '@/hooks/useEscrow'
import { useReleaseEscrow, useCancelDeal, useEditDeal, useSubmitReview } from '@/hooks/useEscrowWrite'
import { parseContractError } from '@/lib/errors'
import { ErrorBanner } from '@/components/ui/ErrorBanner'
import { formatFeePercent } from '@/lib/fee'
import { EscrowStatus } from '@/lib/types'
import { IntroScreen } from '@/components/escrow/IntroScreen'
import { Dropdown } from '@/components/ui/Dropdown'
import { payoutSymbol, payoutDecimals } from '@/lib/tokens'
import { useUsdValue } from '@/hooks/useTokenPrice'
import { EnsName } from '@/components/EnsName'
import { DealReceiptBadge } from '@/components/DealReceiptBadge'
import { useReputation } from '@/hooks/useReputation'
import { getTargetChainId, CHAIN_IDS, getExplorerUrl } from '@/lib/chains'
import { useDynamicAuth } from '@/hooks/useDynamicAuth'
import { MOCK_MODE } from '@/lib/mock'
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

function isValidDealParam(param: string | undefined): param is string {
  if (!param) return false
  const { dealId, escrowAddress } = parseDealParam(param)
  return !!dealId || !!escrowAddress
}


function expiryProgress(expiresAtSec: bigint): number {
  const remainingMs = Number(expiresAtSec) * 1000 - Date.now()
  return Math.max(0, Math.min(100, (remainingMs / SEVEN_DAYS_MS) * 100))
}

// ─── Code input boxes ─────────────────────────────────────────────────────────

interface CodeInputProps {
  value: string[]
  onChange: (chars: string[]) => void
}

function CodeInputBoxes({ value, onChange }: CodeInputProps) {
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  function handleChange(idx: number, raw: string) {
    const char = raw.slice(-1).toUpperCase().replace(/[^A-Z0-9]/, '')
    if (!char) return
    const next = [...value]
    next[idx] = char
    onChange(next)
    if (idx < 3) refs[idx + 1].current?.focus()
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      const next = [...value]
      if (next[idx]) {
        next[idx] = ''
        onChange(next)
      } else if (idx > 0) {
        next[idx - 1] = ''
        onChange(next)
        refs[idx - 1].current?.focus()
      }
      e.preventDefault()
    }
  }

  return (
    <div className="flex justify-center gap-3">
      {value.map((char, i) => (
        <div key={i} className="relative w-14 h-16">
          <input
            ref={refs[i]}
            type="text"
            inputMode="text"
            maxLength={2}
            value={char}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            onFocus={e => e.target.select()}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            aria-label={`Code digit ${i + 1}`}
          />
          <div
            onClick={() => refs[i].current?.focus()}
            className={`w-full h-full rounded-xl flex items-center justify-center text-2xl font-mono font-bold cursor-text transition-colors
              ${char
                ? 'bg-hoff-elevated text-hoff-text-primary border border-hoff-accent/50'
                : 'bg-hoff-elevated text-hoff-text-tertiary border border-hoff-brand'
              }`}
          >
            {char || '–'}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── View Escrow (FUNDED overview) ────────────────────────────────────────────

interface ViewEscrowProps {
  dealIdParam: string
  amount: bigint
  expiresAt: bigint
  seller: string
  sellerEns: string   // forward ENS: stored on-chain at deal creation
  status: EscrowStatus
  description: string
  sym: string
  fmt: (v: bigint) => string
  usdLabel: string
  onUnlock: () => void
}

function ViewEscrowView({ dealIdParam, amount, expiresAt, seller, sellerEns, status, description, sym, fmt, usdLabel, onUnlock }: ViewEscrowProps) {
  const navigate = useNavigate()
  const { reputation } = useReputation(seller as `0x${string}`)
  const payLink = `${window.location.origin}/pay/${dealIdParam}`
  const progress = expiryProgress(expiresAt)

  function handleShare() {
    const text = `Pay me via HandOff: ${payLink}`
    if (typeof navigator.share !== 'undefined') {
      navigator.share({ title: 'HandOff Payment', text, url: payLink }).catch(() => {})
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(payLink).catch(() => {})
  }

  return (
    <main className="w-full px-4 sm:max-w-md sm:mx-auto py-4 space-y-3">

      {/* Title + status */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-hoff-text-primary">View Escrow</h1>
          <StatusBadge status={status} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-hoff-text-tertiary truncate">
            {description || `Unlabelled #${dealIdParam}`}
          </span>
          <span className="text-xs text-hoff-text-tertiary shrink-0">
            Created {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Amount card */}
      <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
        <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest">
          Amount
        </p>

        <div>
          <span className="text-5xl font-bold text-hoff-text-primary tabular-nums">
            {fmt(amount)}
          </span>
          <span className="text-lg font-medium text-hoff-text-tertiary ml-1.5">{sym}</span>
          <p className="text-xs text-hoff-text-tertiary mt-1">{usdLabel}</p>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-hoff-text-tertiary">Expires in</span>
            <CountdownTimer expiresAt={Number(expiresAt) * 1000} />
          </div>
          <div className="h-1.5 bg-hoff-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-hoff-accent rounded-full transition-all duration-1000"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Creator + reputation card */}
      <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-hoff-elevated flex items-center justify-center text-hoff-text-secondary text-xs font-bold shrink-0">
            {seller.slice(2, 4).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
              <p className="text-xs text-hoff-text-tertiary">Creator</p>
              <span className="text-[9px] font-semibold text-hoff-accent bg-hoff-accent/10 px-1.5 py-0.5 rounded-full uppercase tracking-wider">ENS</span>
            </div>
            {/* Forward ENS: seller's ENS name as stored on-chain at deal creation */}
            {sellerEns ? (
              <p className="text-sm text-hoff-text-primary font-medium truncate">{sellerEns}</p>
            ) : (
              /* Reverse ENS: resolve address → name via ENS registry */
              <EnsName
                address={seller as `0x${string}`}
                className="text-sm text-hoff-text-primary font-mono truncate block"
              />
            )}
          </div>
          <a
            href={`${getExplorerUrl()}/address/${seller}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors shrink-0"
            aria-label="View on explorer"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>

        <div className="space-y-2 pt-2 border-t border-hoff-brand">
          <div className="flex items-center justify-between">
            <span className="text-xs text-hoff-text-tertiary">Completed HandOffs</span>
            <span className="text-xs font-medium text-hoff-text-secondary">{reputation.sellerDealCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-hoff-text-tertiary">Volume</span>
            <span className="text-xs font-medium text-hoff-text-secondary">{formatEther(reputation.sellerTotalVolume)} ETH</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-hoff-text-tertiary">Reputation</span>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-hoff-text-secondary font-medium">
                {reputation.sellerTotalReviews > 0
                  ? `${Math.round((reputation.sellerPositiveReviews / reputation.sellerTotalReviews) * 100)}%`
                  : '—'}
              </span>
              <span className="flex items-center gap-0.5 text-hoff-accent">
                <svg width="10" height="10" viewBox="0 0 24 24"><path d="M12 4L20 20H4L12 4Z" fill="#2EBF7A"/></svg>
                {reputation.sellerPositiveReviews}
              </span>
              <span className="flex items-center gap-0.5 text-hoff-text-tertiary">
                <svg width="10" height="10" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="#4B5563"/></svg>
                {reputation.sellerTotalReviews - reputation.sellerPositiveReviews}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <Button fullWidth onClick={onUnlock}>
        Unlock With Code
      </Button>

      <button
        onClick={handleShare}
        className="w-full h-12 rounded-xl border border-hoff-brand text-hoff-text-secondary hover:bg-hoff-surface hover:text-hoff-text-primary transition-colors text-sm font-semibold flex items-center justify-center gap-2"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
        Share HandOff
      </button>

      <button
        onClick={handleCopyLink}
        className="w-full text-xs text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors py-1 text-center"
      >
        Copy payment link
      </button>

      <button
        onClick={() => navigate(`/deal/${dealIdParam}`)}
        className="w-full text-xs text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors py-1 text-center"
      >
        Manage Escrow
      </button>

    </main>
  )
}

// ─── Claim Funds (code entry) ──────────────────────────────────────────────────

interface ClaimFundsProps {
  dealIdParam: string
  amount: bigint
  feeAmount: bigint
  protocolFeeBps: bigint
  expiresAt: bigint
  description: string
  sym: string
  fmt: (v: bigint) => string
  usdLabel: string
  onBack: () => void
  onRelease: (code: string) => void
  isPending: boolean
  isConfirming: boolean
  isError: boolean
  error?: Error | null
}

function ClaimFundsView({
  dealIdParam, amount, feeAmount, protocolFeeBps, expiresAt, description, sym, fmt, usdLabel,
  onBack, onRelease, isPending, isConfirming, isError, error,
}: ClaimFundsProps) {
  const [chars, setChars] = useState(['', '', '', ''])
  const progress = expiryProgress(expiresAt)
  const fullCode = chars.join('')
  const isComplete = fullCode.length === 4 && chars.every(c => c !== '')

  return (
    <main className="w-full px-4 sm:max-w-md sm:mx-auto py-4 space-y-3">

      {/* Back + title */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Back
      </button>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-hoff-text-primary">Claim Funds</h1>
          <StatusBadge status={EscrowStatus.COMPLETED} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-hoff-text-tertiary truncate">
            {description || `Unlabelled #${dealIdParam}`}
          </span>
          <span className="text-xs text-hoff-text-tertiary shrink-0">
            Created {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        </div>
      </div>

      {/* You will receive */}
      <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
        <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest">
          You Will Receive
        </p>
        <div>
          <span className="text-5xl font-bold text-hoff-text-primary tabular-nums">
            {fmt(amount)}
          </span>
          <span className="text-lg font-medium text-hoff-text-tertiary ml-1.5">{sym}</span>
          <p className="text-xs text-hoff-text-tertiary mt-1">{usdLabel}</p>
        </div>
        {feeAmount > 0n && (
          <div className="pt-3 border-t border-hoff-brand flex items-center justify-between text-[11px] text-hoff-text-tertiary">
            <span>Buyer paid {fmt(amount + feeAmount)} {sym}</span>
            <span>{formatFeePercent(protocolFeeBps)} platform fee</span>
          </div>
        )}
      </div>

      {/* Code input card */}
      <div className="bg-hoff-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-hoff-text-secondary">Buyer's 4-Digit Code</p>
          <button
            className="w-6 h-6 rounded-full border border-hoff-text-tertiary/40 flex items-center justify-center text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors"
            title="Ask the buyer to show you their code. Enter it here to release the funds."
            aria-label="What is this code?"
          >
            <span className="text-xs font-semibold">?</span>
          </button>
        </div>

        <CodeInputBoxes value={chars} onChange={setChars} />
      </div>

      {/* Expiry progress */}
      <div className="bg-hoff-surface rounded-2xl px-5 py-4 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-hoff-text-tertiary">Expires in</span>
          <CountdownTimer expiresAt={Number(expiresAt) * 1000} />
        </div>
        <div className="h-1.5 bg-hoff-elevated rounded-full overflow-hidden">
          <div
            className="h-full bg-hoff-accent rounded-full transition-all duration-1000"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {isError && <ErrorBanner error={error} />}

      <Button
        fullWidth
        onClick={() => onRelease(fullCode)}
        disabled={!isComplete || isPending || isConfirming}
        loading={isPending || isConfirming}
      >
        {isPending ? 'Confirm in wallet…' : isConfirming ? 'Releasing funds…' : 'Claim Funds'}
      </Button>

    </main>
  )
}

// ─── Completed view ────────────────────────────────────────────────────────────

interface ReviewState {
  isPending: boolean
  isConfirming: boolean
  isSuccess: boolean
  isError: boolean
  error: unknown
}

interface CompletedProps {
  dealIdParam: string
  dealId?: bigint
  amount: bigint
  description: string
  sym: string
  fmt: (v: bigint) => string
  usdLabel: string
  txHash?: string
  escrowAddress?: string
  onSubmitReview?: (isPositive: boolean) => void
  reviewState: ReviewState
}

function CompletedView({ dealIdParam, dealId, amount, description, sym, fmt, usdLabel, txHash, escrowAddress, onSubmitReview, reviewState }: CompletedProps) {
  const [review, setReview] = useState<'positive' | 'negative' | null>(null)
  const [submitted, setSubmitted] = useState(false)

  // Mark submitted only after on-chain confirmation — never speculatively
  useEffect(() => {
    if (reviewState.isSuccess) setSubmitted(true)
  }, [reviewState.isSuccess])

  // Reset submitted on error so the seller can retry
  useEffect(() => {
    if (reviewState.isError) setSubmitted(false)
  }, [reviewState.isError])
  const etherscanBase = getTargetChainId() === CHAIN_IDS.MAINNET
    ? 'https://etherscan.io/'
    : 'https://sepolia.etherscan.io/'
  const etherscanHref = txHash
    ? `${etherscanBase}tx/${txHash}`
    : escrowAddress
      ? `${etherscanBase}address/${escrowAddress}`
      : undefined

  return (
    <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6 space-y-5">

      {/* Checkmark */}
      <div className="flex flex-col items-center gap-3 pt-2">
        <div className="w-16 h-16 rounded-full bg-hoff-accent/20 border-2 border-hoff-accent flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2EBF7A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-hoff-text-primary text-center">HandOff Completed</h1>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-hoff-text-tertiary truncate">
          {description || `Unlabelled #${dealIdParam}`}
        </span>
        <span className="text-xs text-hoff-text-tertiary shrink-0 ml-2">
          Created {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </span>
      </div>

      {/* Amount received */}
      <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
        <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest">
          You Will Receive
        </p>
        <div>
          <span className="text-5xl font-bold text-hoff-text-primary tabular-nums">
            {fmt(amount)}
          </span>
          <span className="text-lg font-medium text-hoff-text-tertiary ml-1.5">{sym}</span>
          <p className="text-xs text-hoff-text-tertiary mt-1">{usdLabel}</p>
        </div>
      </div>

      {/* ENS Receipt Badge */}
      <DealReceiptBadge dealIdParam={dealIdParam} />

      {/* Etherscan */}
      {etherscanHref && (
        <a
          href={etherscanHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border border-hoff-text-tertiary/30 text-sm text-hoff-text-secondary hover:text-hoff-text-primary hover:border-hoff-text-secondary/50 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          {txHash ? 'View on Etherscan' : 'View Contract on Etherscan'}
        </a>
      )}

      {/* ENS Receipt — minted on-chain via HandOffSubnameRegistrar */}
      {dealId !== undefined && (
        <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2EBF7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
            </svg>
            <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest">ENS Receipt</p>
          </div>
          <p className="text-sm font-mono text-hoff-accent break-all">
            deal-{String(dealId)}.hand-off.eth
          </p>
          <p className="text-xs text-hoff-text-tertiary">
            Minted on-chain as a permanent record of this transaction
          </p>
          <a
            href={`https://app.ens.domains/deal-${String(dealId)}.hand-off.eth`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-hoff-text-tertiary hover:text-hoff-accent transition-colors"
          >
            View on ENS
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
      )}

      {/* Review */}
      {!submitted ? (
        <div className="space-y-3">
          <p className="text-sm text-hoff-text-tertiary text-center">Leave a Review – How did it go?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setReview('positive')}
              disabled={reviewState.isPending || reviewState.isConfirming}
              className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-2 border transition-all disabled:opacity-50 ${
                review === 'positive'
                  ? 'bg-hoff-accent/20 border-hoff-accent text-hoff-accent'
                  : 'bg-hoff-surface border-hoff-surface text-hoff-text-secondary hover:border-hoff-accent/40'
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M12 4L20 20H4L12 4Z" fill={review === 'positive' ? '#2EBF7A' : 'none'} stroke={review === 'positive' ? '#2EBF7A' : '#6B7B7B'} strokeWidth="2" strokeLinejoin="round"/>
              </svg>
              <span className="text-xs font-medium">Positive</span>
            </button>
            <button
              onClick={() => setReview('negative')}
              disabled={reviewState.isPending || reviewState.isConfirming}
              className={`h-20 rounded-2xl flex flex-col items-center justify-center gap-2 border transition-all disabled:opacity-50 ${
                review === 'negative'
                  ? 'bg-hoff-err-bg border-hoff-err text-hoff-err'
                  : 'bg-hoff-surface border-hoff-surface text-hoff-text-secondary hover:border-hoff-err/40'
              }`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M12 20L4 4H20L12 20Z" fill={review === 'negative' ? '#f87171' : 'none'} stroke={review === 'negative' ? '#f87171' : '#6B7B7B'} strokeWidth="2" strokeLinejoin="round"/>
              </svg>
              <span className="text-xs font-medium">Negative</span>
            </button>
          </div>
          {reviewState.isError && <ErrorBanner error={reviewState.error} />}
          <button
            onClick={() => {
              if (!review || reviewState.isPending || reviewState.isConfirming) return
              onSubmitReview?.(review === 'positive')
            }}
            disabled={!review || reviewState.isPending || reviewState.isConfirming}
            className="w-full h-12 rounded-xl bg-hoff-accent text-hoff-accent-fg font-bold text-sm disabled:opacity-40 hover:bg-hoff-accent-hover transition-colors flex items-center justify-center gap-2"
          >
            {(reviewState.isPending || reviewState.isConfirming) ? (
              <>
                <Spinner size="sm" />
                <span>{reviewState.isPending ? 'Confirm in wallet…' : 'Submitting review…'}</span>
              </>
            ) : 'Submit'}
          </button>
        </div>
      ) : (
        <div className="bg-hoff-accent/10 border border-hoff-accent/30 rounded-xl px-4 py-3 text-center">
          <p className="text-sm text-hoff-accent font-medium">Thanks for your review!</p>
        </div>
      )}

    </main>
  )
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function ManageDeal() {
  const { dealId: dealIdParam } = useParams<{ dealId: string }>()
  const navigate = useNavigate()
  const { address: wagmiAddress } = useAccount()
  const { walletAddress: dynamicAddress } = useDynamicAuth()
  const [showCodeEntry, setShowCodeEntry] = useState(false)
  const [showIntro, setShowIntro] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [editAmount, setEditAmount] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editExpirationHours, setEditExpirationHours] = useState(168)

  const { dealId, escrowAddress: directAddress } = parseDealParam(dealIdParam)

  // ─── All hooks unconditionally — rules of hooks require no conditional calls ──
  const dealIdOrZero = dealId ?? 0n

  const { details, isLoading, isError, escrowAddress }                              = useDealDetails(dealId, directAddress)
  const { release, isPending, isConfirming, isSuccess, isError: releaseError, error: releaseErrorObj, txHash } = useReleaseEscrow(dealIdOrZero, escrowAddress)
  const cancelDeal = useCancelDeal(dealIdOrZero, escrowAddress)
  const editDeal   = useEditDeal(dealIdOrZero, escrowAddress)
  const reviewHook = useSubmitReview(escrowAddress)

  // Token display helpers
  const sym = details ? payoutSymbol(details.payoutToken) : 'ETH'
  const dec = details ? payoutDecimals(details.payoutToken) : 18
  const fmt = (v: bigint) => formatUnits(v, dec)
  const usdRaw = useUsdValue(details?.amount ?? 0n, details?.payoutToken ?? null)
  const usdLabel = usdRaw ? `≈ $${usdRaw} USD` : ''

  // Use Dynamic's walletAddress (always up-to-date) with wagmi as fallback —
  // wagmi's address can be undefined when connected via Dynamic SDK.
  const connectedAddress = dynamicAddress ?? wagmiAddress
  const isSeller = MOCK_MODE || !!(connectedAddress && details && connectedAddress.toLowerCase() === details.seller.toLowerCase())

  // ─── Guard (after all hooks) ───────────────────────────────────────────────
  if (!isValidDealParam(dealIdParam)) {
    return (
      <Layout>
        <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6">
          <div className="bg-hoff-surface rounded-2xl p-5">
            <p className="text-sm text-hoff-err">Invalid deal link. Check the URL and try again.</p>
          </div>
        </main>
      </Layout>
    )
  }

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24"><Spinner /></div>
      </Layout>
    )
  }

  // ─── Error / not found ──────────────────────────────────────────────────────
  if (isError || !details) {
    return (
      <Layout>
        <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6">
          <div className="bg-hoff-surface rounded-2xl p-5">
            <p className="text-sm text-hoff-err">Could not load deal. Check the link and try again.</p>
          </div>
        </main>
      </Layout>
    )
  }

  // ─── Intro screen ──────────────────────────────────────────────────────────
  if (showIntro) {
    return (
      <Layout>
        <IntroScreen onContinue={() => setShowIntro(false)} />
      </Layout>
    )
  }

  // ─── Completed (after successful release OR on-chain COMPLETE) ──────────────
  if (isSuccess || details.status === EscrowStatus.COMPLETED) {
    return (
      <Layout>
        <CompletedView
          dealIdParam={dealIdParam}
          dealId={dealId ?? undefined}
          amount={details.amount}
          description={details.description}
          sym={sym}
          fmt={fmt}
          usdLabel={usdLabel}
          txHash={txHash}
          escrowAddress={escrowAddress}
          onSubmitReview={(isPositive) => reviewHook.submitReview(isPositive)}
          reviewState={reviewHook}
        />
      </Layout>
    )
  }

  // ─── Refunded ───────────────────────────────────────────────────────────────
  if (details.status === EscrowStatus.EXPIRED) {
    return (
      <Layout>
        <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6 space-y-5">
          <div className="flex flex-col items-center gap-3 pt-2">
            <div className="w-16 h-16 rounded-full bg-hoff-elevated border-2 border-hoff-text-tertiary/40 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6B7B7B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-hoff-text-primary text-center">HandOff Refunded</h1>
            <p className="text-sm text-hoff-text-tertiary text-center">
              The buyer has reclaimed their funds after the deal expired.
            </p>
          </div>
        </main>
      </Layout>
    )
  }

  // ─── CANCELED ───────────────────────────────────────────────────────────────
  if (details.status === EscrowStatus.CANCELED) {
    return (
      <Layout>
        <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6 space-y-5">
          <div className="flex flex-col items-center gap-3 pt-2">
            <div className="w-16 h-16 rounded-full bg-hoff-err-bg border-2 border-hoff-err/40 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-hoff-text-primary text-center">HandOff Canceled</h1>
            <p className="text-sm text-hoff-text-tertiary text-center">
              This deal was canceled before the buyer funded it. No funds were moved.
            </p>
          </div>
          <Button fullWidth variant="ghost" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </main>
      </Layout>
    )
  }

  // ─── PENDING — waiting for buyer + edit/cancel ──────────────────────────────
  if (details.status === EscrowStatus.CREATED) {
    const payLink = `${window.location.origin}/pay/${dealIdParam}`

    // ── Cancel confirmation ───────────────────────────────────────────────────
    if (showCancelConfirm) {
      return (
        <Layout>
          <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6 space-y-4">
            <h1 className="text-2xl font-bold text-hoff-text-primary">Cancel HandOff?</h1>
            <p className="text-sm text-hoff-text-tertiary">
              This will permanently cancel the deal. The payment link will stop working.
              No funds have been deposited, so nothing needs to be refunded.
            </p>

            {cancelDeal.isError && <ErrorBanner error={cancelDeal.error} />}

            <Button
              fullWidth
              variant="danger"
              onClick={() => cancelDeal.cancel()}
              loading={cancelDeal.isPending || cancelDeal.isConfirming}
            >
              {cancelDeal.isPending ? 'Confirm in wallet…' : cancelDeal.isConfirming ? 'Canceling…' : 'Yes, Cancel This HandOff'}
            </Button>
            <Button
              fullWidth
              variant="ghost"
              onClick={() => setShowCancelConfirm(false)}
            >
              Go Back
            </Button>
          </main>
        </Layout>
      )
    }

    // ── Edit form ─────────────────────────────────────────────────────────────
    if (showEdit) {
      const amountValid = (() => {
        if (!editAmount) return false
        try { return parseUnits(editAmount as `${number}`, dec) > 0n } catch { return false }
      })()

      return (
        <Layout>
          <main className="w-full px-4 sm:max-w-md sm:mx-auto py-6 space-y-3">
            <button
              onClick={() => setShowEdit(false)}
              className="flex items-center gap-1.5 text-xs text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back
            </button>

            <h1 className="text-2xl font-bold text-hoff-text-primary">Edit HandOff</h1>

            {/* Amount */}
            <div className="bg-hoff-surface rounded-2xl p-5">
              <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest mb-2">Amount</p>
              <div className="flex items-start justify-between gap-3">
                <input
                  type="text"
                  inputMode="decimal"
                  value={editAmount}
                  onChange={e => setEditAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full text-4xl font-semibold bg-transparent text-hoff-text-primary placeholder:text-hoff-text-tertiary/40 focus:outline-none"
                />
                <span className="text-hoff-text-secondary font-medium text-sm shrink-0 mt-3">{sym}</span>
              </div>
            </div>

            {/* Description */}
            <div className="bg-hoff-surface rounded-2xl p-5">
              <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest mb-3">
                Description <span className="normal-case font-normal">(Optional)</span>
              </p>
              <input
                type="text"
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                placeholder="E.g. iPhone 14 Pro"
                className="w-full bg-transparent text-hoff-text-primary text-sm placeholder:text-hoff-text-tertiary focus:outline-none"
              />
            </div>

            {/* Expiration */}
            <div className="bg-hoff-surface rounded-2xl p-5">
              <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest mb-2">
                New Expiration
              </p>
              <Dropdown
                value={String(editExpirationHours)}
                onChange={v => setEditExpirationHours(Number(v))}
                options={[
                  { label: '1 Day',   value: '24' },
                  { label: '3 Days',  value: '72' },
                  { label: '7 Days',  value: '168' },
                  { label: '14 Days', value: '336' },
                  { label: '30 Days', value: '720' },
                ]}
              />
            </div>

            {editDeal.isError && <ErrorBanner error={editDeal.error} />}

            {!editDeal.isSuccess ? (
              <Button
                fullWidth
                onClick={() => {
                  if (!amountValid) return
                  const newExpiry = BigInt(Math.floor(Date.now() / 1000) + editExpirationHours * 3600)
                  editDeal.edit(
                    parseUnits(editAmount as `${number}`, dec),
                    editDescription,
                    (details?.payoutToken ?? '0x0000000000000000000000000000000000000000') as `0x${string}`,
                    (details?.seller ?? '0x0000000000000000000000000000000000000000') as `0x${string}`,
                    newExpiry,
                  )
                }}
                disabled={!amountValid || editDeal.isPending || editDeal.isConfirming}
                loading={editDeal.isPending || editDeal.isConfirming}
              >
                {editDeal.isPending ? 'Confirm in wallet…' : editDeal.isConfirming ? 'Updating…' : 'Save Changes'}
              </Button>
            ) : (
              <Button fullWidth variant="ghost" onClick={() => setShowEdit(false)}>
                Done
              </Button>
            )}
          </main>
        </Layout>
      )
    }

    // ── Default PENDING view ──────────────────────────────────────────────────
    return (
      <Layout>
        <main className="w-full px-4 sm:max-w-md sm:mx-auto py-4 space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-hoff-text-primary">View Escrow</h1>
              <StatusBadge status={details.status} />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-hoff-text-tertiary truncate">
                {details.description || `Unlabelled #${dealIdParam}`}
              </span>
              <span className="text-xs text-hoff-text-tertiary shrink-0">
                Created {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </span>
            </div>
          </div>

          <div className="bg-hoff-surface rounded-2xl p-5 space-y-3">
            <p className="text-xs font-semibold text-hoff-text-tertiary uppercase tracking-widest">Amount</p>
            <div>
              <span className="text-5xl font-bold text-hoff-text-primary tabular-nums">
                {fmt(details.amount)}
              </span>
              <span className="text-lg font-medium text-hoff-text-tertiary ml-1.5">{sym}</span>
            </div>
            <div className="space-y-1.5 pt-1 border-t border-hoff-brand">
              <div className="flex items-center justify-between">
                <span className="text-xs text-hoff-text-tertiary">Expires in</span>
                <CountdownTimer expiresAt={Number(details.expiresAt) * 1000} />
              </div>
              <div className="h-1.5 bg-hoff-elevated rounded-full overflow-hidden">
                <div className="h-full bg-hoff-accent rounded-full" style={{ width: `${expiryProgress(details.expiresAt)}%` }} />
              </div>
            </div>
          </div>

          {/* Waiting indicator */}
          <div className="bg-hoff-warn-bg border border-hoff-warn/20 rounded-xl px-4 py-3 flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-sm text-hoff-warn">Waiting for buyer to pay…</span>
          </div>

          {/* Share link */}
          <button
            onClick={() => {
              const text = `Pay me via HandOff: ${payLink}`
              if (typeof navigator.share !== 'undefined') {
                navigator.share({ title: 'HandOff Payment', text, url: payLink }).catch(() => {})
              } else {
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
              }
            }}
            className="w-full h-12 rounded-xl border border-hoff-brand text-hoff-text-secondary hover:bg-hoff-surface hover:text-hoff-text-primary transition-colors text-sm font-semibold flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Share HandOff
          </button>

          <button
            onClick={() => navigator.clipboard.writeText(payLink).catch(() => {})}
            className="w-full text-xs text-hoff-text-tertiary hover:text-hoff-text-secondary transition-colors py-1 text-center"
          >
            Copy payment link
          </button>

          {/* Edit + Cancel (UC-2 / UC-3 — only for seller, only PENDING) */}
          {isSeller && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  setEditAmount(formatUnits(details.amount, dec))
                  setEditDescription(details.description)
                  setEditExpirationHours(168)
                  setShowEdit(true)
                }}
              >
                Edit
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={() => setShowCancelConfirm(true)}
              >
                Cancel
              </Button>
            </div>
          )}
        </main>
      </Layout>
    )
  }

  // ─── FUNDED — code entry or overview ────────────────────────────────────────
  if (showCodeEntry && isSeller) {
    return (
      <Layout>
        <ClaimFundsView
          dealIdParam={dealIdParam}
          amount={details.amount}
          feeAmount={details.feeAmount}
          protocolFeeBps={details.protocolFeeBps}
          expiresAt={details.expiresAt}
          description={details.description}
          sym={sym}
          fmt={fmt}
          usdLabel={usdLabel}
          onBack={() => setShowCodeEntry(false)}
          onRelease={(code) => release(code)}
          isPending={isPending}
          isConfirming={isConfirming}
          isError={releaseError}
          error={releaseErrorObj}
        />
      </Layout>
    )
  }

  return (
    <Layout>
      <ViewEscrowView
        dealIdParam={dealIdParam}
        amount={details.amount}
        expiresAt={details.expiresAt}
        seller={details.seller}
        sellerEns={details.sellerEns ?? ''}
        status={details.status}
        description={details.description}
        sym={sym}
        fmt={fmt}
        usdLabel={usdLabel}
        onUnlock={() => isSeller ? setShowCodeEntry(true) : undefined}
      />
    </Layout>
  )
}
